import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { organisationsApi } from '~/services/organisations-api/organisationsApi';

import { Organisation, OrganisationsPage } from '~/services/organisations-api/types';

export type OrganisationContextType = {
  organisation: Organisation | undefined;
  organisations: OrganisationsPage | undefined;
  onUpdateActiveOrganisation: (organisationId: string) => Promise<boolean>;
};

export const OrganisationContext = createContext<OrganisationContextType>({
  organisation: undefined,
  organisations: undefined,
  onUpdateActiveOrganisation: async () => false,
});

export type OrganisationProviderProps = {
  children: ReactNode;
};

export function OrganisationProvider({ children }: OrganisationProviderProps) {
  const { data: organisation } = useQuery(organisationsApi.queryOptions('get', '/active-organisation'));
  const { data: organisations } = useQuery(organisationsApi.queryOptions('get', '/organisations'));
  const queryClient = useQueryClient();

  const { mutateAsync: updateActiveOrganisation } = organisationsApi.useMutation(
    'put',
    '/organisations/{organisation_id}/active',
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        return true;
      },
      onError: () => false,
    }
  );

  const onUpdateActiveOrganisation = useCallback(
    async (organisationId: string) => {
      try {
        await updateActiveOrganisation({
          params: {
            path: {
              organisation_id: organisationId,
            },
          },
        });
        return true;
      } catch {
        return false;
      }
    },
    [updateActiveOrganisation]
  );

  const value = useMemo(
    () => ({ organisation: organisation, organisations: organisations, onUpdateActiveOrganisation }),
    [organisation, organisations, onUpdateActiveOrganisation]
  );

  return <OrganisationContext.Provider value={value}>{children}</OrganisationContext.Provider>;
}

export function useOrganisation() {
  const { organisation, organisations, onUpdateActiveOrganisation } = useContext(OrganisationContext);

  return { organisation, organisations, onUpdateActiveOrganisation };
}
